import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

     const { signal } = this.#controller;  

    const ribbonMap = this.querySelector('[data-ribbon-design-map]');
    if (ribbonMap) {
      this.#ribbonDesignMap = JSON.parse(ribbonMap.textContent || '{}');
      this.addEventListener('click', this.#handleRibbonImageClick, { capture: true, signal });
      this.addEventListener('slideshow:select', (event) => {
        if (event.detail?.userInitiated && ['scroll', 'drag'].includes(event.detail.trigger)) {
          this.#selectRibbonMedia(event.detail.slide?.dataset.ribbonMediaId);
        }
      }, { signal });
      this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, (event) => {
        this.#selectRibbonMedia(this.#ribbonDesignMap.media?.[event.detail.index]);
      }, { signal });
    }

this.addEventListener('slideshow:select', (event) => {
      const slide = event.detail?.slide;

      // Immediately stop audio and motion whenever another image or video is selected.
      for (const otherMedia of this.querySelectorAll('.product-media-container--video deferred-media')) {
        if (!slide?.contains(otherMedia)) otherMedia.pauseMedia?.();
      }

      if (!slide?.classList.contains('product-media-container--video')) return;

      const media = slide.querySelector('deferred-media');
      if (!media) return;

      const alreadyLoaded = media.hasAttribute('data-media-loaded');

      // Shopify's deferred media normally auto-plays its video clone.
      // Disable that first attempt: the gallery starts the visible video once.
      if (!alreadyLoaded) {
        const templateVideo = media.querySelector('template')?.content.querySelector('video');
        if (templateVideo) {
          templateVideo.autoplay = false;
          templateVideo.preload = 'auto';
        }
      }

      media.showDeferredMedia?.();

      const videos = media.querySelectorAll('video');
      const video = videos[videos.length - 1];
      if (!video) return;

      video.playsInline = true;
      video.preload = 'auto';
      video.muted = false;
      video.controls = false;

      // Keep the still preview in place until actual video imagery arrives.
      // This avoids exposing a black or unmoving first frame while Safari buffers.
      if (!alreadyLoaded) {
        const poster = media.querySelector('.deferred-media__poster-button');
        const playIcon = poster?.querySelector('.deferred-media__poster-icon');
        playIcon && (playIcon.style.display = 'none');
        poster?.classList.remove('deferred-media__playing');
        const revealVideo = () => {
          poster?.classList.add('deferred-media__playing');
          playIcon?.style.removeProperty('display');
        };

        if (typeof video.requestVideoFrameCallback === 'function') {
          video.requestVideoFrameCallback(revealVideo);
        } else if (video.readyState >= 2) {
          revealVideo();
        } else {
          video.addEventListener('loadeddata', revealVideo, { once: true });
        }
      }

      video.addEventListener('click', () => {
        video.controls = true;
      }, { once: true });

      if (video.paused) {
        video.play().catch(() => {
          // If the browser blocks sound-on playback, show manual controls.
          media.querySelector('.deferred-media__poster-button')?.classList.add('deferred-media__playing');
          video.controls = true;
        });
      }
    }, { signal });

    // Stop playback when the gallery leaves the screen while scrolling the page.
    // The grid observer also handles galleries that display multiple media tiles.
    if (typeof IntersectionObserver !== 'undefined') {
      this.#videoVisibilityObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) continue;
          entry.target.querySelectorAll('deferred-media').forEach((media) => media.pauseMedia?.());
        }
      }, { threshold: 0 });

      this.#videoVisibilityObserver.observe(this);
      this.querySelectorAll('.media-gallery__grid .product-media-container--video').forEach((tile) => {
        this.#videoVisibilityObserver.observe(tile);
      });
    }

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) return;
      this.querySelectorAll('.product-media-container--video deferred-media').forEach((media) => media.pauseMedia?.());
    }, { signal });

    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #videoVisibilityObserver;
  #ribbonDesignMap = {};

  #handleRibbonImageClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumbnail = event.target.closest('.slideshow-controls__thumbnail, .dialog-thumbnails-list__thumbnail');
    if (thumbnail) {
      const index = Number(thumbnail.getAttribute('on:click')?.split('/').pop());
      if (Number.isInteger(index)) this.#selectRibbonMedia(this.#ribbonDesignMap.media?.[index]);
      return;
    }
    const tile = event.target.closest('[data-ribbon-media-id]');
    if (tile) this.#selectRibbonMedia(tile.dataset.ribbonMediaId);
  };

  #selectRibbonMedia(mediaId) {
    if (mediaId == null) return;
    const section = this.closest('.shopify-section, dialog');
    const pickers = Array.from(section?.querySelectorAll('variant-picker') ?? []);
    const picker = pickers.find((candidate) => candidate.dataset.productId === String(this.#ribbonDesignMap.productId));
    picker?.selectSkirtMedia?.(mediaId);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#videoVisibilityObserver?.disconnect();
    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}


